import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './auth'

interface WsEvent {
  type: string
  payload?: unknown
}

const INVALIDATIONS: Record<string, string[][]> = {
  'bet.placed': [['bets'], ['phase-status'], ['account']],
  'bet.settled': [['bets'], ['phase-status'], ['account']],
  'bet.cancelled': [['bets'], ['phase-status']],
  'phase.changed': [['phase-status'], ['account'], ['me']],
  'account.closed': [['account'], ['phase-status'], ['me']],
  'snapshot.updated': [['daily-snapshots'], ['phase-status']],
  'score.updated': [['score'], ['score-history']],
  'cycle.updated': [['funded-cycle'], ['funded-cycles']],
  'payout.updated': [['payouts'], ['funded-cycle']],
  'notification.new': [['notifications']],
}

export function useWsEvents(): void {
  const qc = useQueryClient()
  const { user } = useAuth()
  const ref = useRef<WebSocket | null>(null)

  useEffect(() => {
    if (!user) return
    // Build the WS URL from VITE_API_URL when it's set (cross-origin prod:
    // the API lives on api.rivalprop.com, the SPA on beta.rivalprop.com).
    // Same-origin dev/CI falls back to window.location.host.
    const apiBase = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
    let url: string
    if (apiBase) {
      url = apiBase.replace(/^http/, 'ws') + '/ws'
    } else {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      url = `${proto}://${window.location.host}/ws`
    }
    let closed = false
    let sock: WebSocket | null = null
    try {
      sock = new WebSocket(url)
      ref.current = sock
    } catch {
      return
    }

    sock.onmessage = (ev: MessageEvent<string>) => {
      try {
        const parsed = JSON.parse(ev.data) as WsEvent
        const keys = INVALIDATIONS[parsed.type]
        if (keys) {
          for (const k of keys) void qc.invalidateQueries({ queryKey: k })
        }
      } catch {
        // ignore
      }
    }
    sock.onerror = () => {
      // silent — proxy may not be up in static build
    }

    // Heartbeat — the server logs a ping handler that responds with pong; if
    // no traffic flows for ~30s an upstream proxy (Caddy, browser) can drop
    // the idle connection. Sending {type:'ping'} every 25s keeps the link warm.
    const heartbeat = window.setInterval(() => {
      if (sock && sock.readyState === WebSocket.OPEN) {
        try { sock.send(JSON.stringify({ type: 'ping' })) } catch { /* ignore */ }
      }
    }, 25_000)

    return () => {
      closed = true
      void closed
      window.clearInterval(heartbeat)
      if (sock && sock.readyState === WebSocket.OPEN) sock.close()
    }
  }, [user, qc])
}
